import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Users, Music, TrendingUp } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { musicalProgressService } from '../../services/musicalProgressService';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

export default function MusicalProgressPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const musicalId = searchParams?.get('id');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [musical, setMusical] = useState(null);
  const [roles, setRoles] = useState([]);
  const [songs, setSongs] = useState([]);

  useEffect(() => {
    if (musicalId && user?.id) {
      loadMusicalData();
    } else if (!musicalId) {
      setError('Musical ID is required');
      setLoading(false);
    }
  }, [musicalId, user]);

  const loadMusicalData = async () => {
    try {
      setLoading(true);
      setError('');

      const [musicalData, rolesData, songsData] = await Promise.all([
        musicalProgressService?.getMusicalDetails(musicalId),
        musicalProgressService?.getMusicalRoles(musicalId, user?.id),
        musicalProgressService?.getMusicalSongs(musicalId, user?.id)
      ]);

      setMusical(musicalData);
      setRoles(rolesData);
      setSongs(songsData);
    } catch (err) {
      console.error('Error loading musical data:', err);
      setError(err?.message || 'Failed to load musical data');
    } finally {
      setLoading(false);
    }
  };

  const navigateToRole = (roleId) => {
    navigate(`/role-detail-page?id=${roleId}`);
  };

  const navigateToSong = (songId) => {
    navigate(`/song-detail-page?id=${songId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error || !musical) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-500/10 border border-red-500 rounded-xl p-6">
            <p className="text-red-200">{error || 'Musical not found'}</p>
            <button
              onClick={() => navigate('/the-stage-student-dashboard')}
              className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{musical?.title} - Progress | RoleCall</title>
        <meta name="description" content={`Track your progress in ${musical?.title} - explore roles, songs, and lessons`} />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-[#6e32b9] to-[#32215d]">
        <Header />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/the-stage-student-dashboard')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>

          {/* Musical Header */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <div className="flex gap-6">
              {/* Poster */}
              <div className="flex-shrink-0">
                <img
                  src={musical?.posterUrl || '/assets/images/no_image.png'}
                  alt={musical?.title}
                  className="w-48 h-72 object-cover rounded-xl shadow-2xl"
                />
              </div>

              {/* Musical Info */}
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-3">{musical?.title}</h1>
                
                {/* Details */}
                <div className="space-y-2 mb-4">
                  {musical?.composer && (
                    <p className="text-purple-200 text-sm">
                      <span className="font-semibold">Music:</span> {musical?.composer}
                    </p>
                  )}
                  {musical?.lyricist && (
                    <p className="text-purple-200 text-sm">
                      <span className="font-semibold">Lyrics:</span> {musical?.lyricist}
                    </p>
                  )}
                  {musical?.bookWriter && (
                    <p className="text-purple-200 text-sm">
                      <span className="font-semibold">Book:</span> {musical?.bookWriter}
                    </p>
                  )}
                  {musical?.yearPremiered && (
                    <p className="text-purple-200 text-sm">
                      <span className="font-semibold">Premiered:</span> {musical?.yearPremiered}
                    </p>
                  )}
                  {musical?.genre && (
                    <span className="inline-block px-3 py-1 bg-purple-500/30 text-purple-200 rounded-lg text-sm">
                      {musical?.genre}
                    </span>
                  )}
                </div>

                {/* Description */}
                {musical?.description && (
                  <div className="bg-white/5 rounded-xl p-4">
                    <p className="text-white/80 text-sm leading-relaxed">{musical?.description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Roles Section */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 mb-8 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-purple-400" />
              Roles in {musical?.title}
            </h2>
            
            {roles?.length === 0 ? (
              <p className="text-white/60 text-center py-8">No roles available yet</p>
            ) : (
              <div className="overflow-x-auto">
                <div className="flex gap-4 pb-4">
                  {roles?.map((role) => (
                    <div
                      key={role?.id}
                      onClick={() => navigateToRole(role?.id)}
                      className="flex-shrink-0 w-56 bg-white/5 rounded-xl p-4 cursor-pointer hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-white font-semibold text-lg">{role?.characterName}</h3>
                        {role?.isPrincipal && (
                          <span className="px-2 py-1 bg-yellow-500/30 text-yellow-200 rounded text-xs">
                            Lead
                          </span>
                        )}
                      </div>
                      
                      <div className="flex gap-2 mb-3">
                        {role?.voiceType && (
                          <span className="px-2 py-1 bg-purple-500/30 text-purple-200 rounded text-xs">
                            {role?.voiceType}
                          </span>
                        )}
                        {role?.roleType && (
                          <span className="px-2 py-1 bg-blue-500/30 text-blue-200 rounded text-xs">
                            {role?.roleType}
                          </span>
                        )}
                      </div>

                      {role?.vocalRange && (
                        <p className="text-white/70 text-xs mb-2">Range: {role?.vocalRange}</p>
                      )}

                      {role?.ageRange && (
                        <p className="text-white/70 text-xs mb-3">Age: {role?.ageRange}</p>
                      )}

                      {/* Progress Indicator */}
                      {role?.progress?.total > 0 && (
                        <div>
                          <div className="flex justify-between text-xs text-white/60 mb-1">
                            <span>Progress</span>
                            <span>{role?.progress?.completed}/{role?.progress?.total}</span>
                          </div>
                          <div className="bg-white/10 rounded-full h-2 overflow-hidden">
                            <div
                              className="bg-gradient-to-r from-purple-400 to-pink-500 h-full rounded-full transition-all"
                              style={{ width: `${(role?.progress?.completed / role?.progress?.total) * 100}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {role?.description && (
                        <p className="text-white/60 text-xs mt-3 line-clamp-2">{role?.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Songs Section */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Music className="w-6 h-6 text-orange-400" />
              Songs in {musical?.title}
            </h2>

            {songs?.length === 0 ? (
              <p className="text-white/60 text-center py-8">No songs available yet</p>
            ) : (
              <div className="space-y-3">
                {songs?.map((song) => (
                  <div
                    key={song?.id}
                    onClick={() => navigateToSong(song?.id)}
                    className="flex items-center gap-4 bg-white/5 rounded-xl p-4 cursor-pointer hover:bg-white/10 transition-colors"
                  >
                    {/* Song Icon */}
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-orange-500 to-pink-500 rounded-lg flex items-center justify-center">
                      <Music className="w-6 h-6 text-white" />
                    </div>

                    {/* Song Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-semibold truncate">{song?.title}</h3>
                      <div className="flex gap-3 text-xs text-white/60 mt-1">
                        {song?.actNumber && <span>Act {song?.actNumber}</span>}
                        {song?.keySignature && <span>Key: {song?.keySignature}</span>}
                      </div>
                    </div>

                    {/* Progress */}
                    <div className="flex-shrink-0 w-40">
                      <div className="flex justify-between text-xs text-white/60 mb-1">
                        <span>Lessons</span>
                        <span>{song?.lessonsCompleted}/{song?.totalLessons}</span>
                      </div>
                      <div className="bg-white/10 rounded-full h-2 overflow-hidden mb-2">
                        <div
                          className="bg-gradient-to-r from-green-400 to-emerald-500 h-full rounded-full transition-all"
                          style={{ width: `${song?.completionPercentage}%` }}
                        />
                      </div>
                      <div className="flex gap-2">
                        {song?.quizCompleted > 0 && (
                          <span className="px-2 py-1 bg-blue-500/30 text-blue-200 rounded text-xs">
                            {song?.quizCompleted} quiz{song?.quizCompleted !== 1 ? 'zes' : ''}
                          </span>
                        )}
                        {song?.quizPerfect > 0 && (
                          <span className="px-2 py-1 bg-yellow-500/30 text-yellow-200 rounded text-xs flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            {song?.quizPerfect} perfect
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}